import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

const SITE_URL = 'https://funcomunion.es';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;

const PRODUCTS = [
  {
    key: 'mini',
    fallbackPrice: 24.90,
    name: { es: 'Figura Mini personalizada', en: 'Custom Mini figurine', it: 'Statuetta Mini personalizzata', pt: 'Figura Mini personalizada' },
    desc: {
      es: 'Figura coleccionable Mini para Primera Comunión personalizada con foto. Estilo Funko Pop, Chibi o Bloques. Pintada a mano en Ciudad Real.',
      en: 'Mini collectible figurine for First Communion personalized from a photo. Funko Pop, Chibi or Blocks style. Hand-painted in Spain.',
      it: 'Statuetta Mini da collezione per Prima Comunione personalizzata da foto. Stile Funko Pop, Chibi o Blocchi. Dipinta a mano in Spagna.',
      pt: 'Figura Mini de coleção para Primeira Comunhão personalizada a partir de foto. Estilo Funko Pop, Chibi ou Blocos. Pintada à mão em Espanha.',
    },
  },
  {
    key: 'classic',
    fallbackPrice: 39.40,
    name: { es: 'Figura Classic personalizada', en: 'Custom Classic figurine', it: 'Statuetta Classic personalizzata', pt: 'Figura Classic personalizada' },
    desc: {
      es: 'Figura coleccionable Classic para Primera Comunión personalizada con foto. La más vendida. Estilo Funko Pop, Chibi o Bloques.',
      en: 'Classic collectible figurine for First Communion personalized from a photo. Our best seller.',
      it: 'Statuetta Classic da collezione per Prima Comunione personalizzata da foto. La più venduta.',
      pt: 'Figura Classic de coleção para Primeira Comunhão personalizada a partir de foto. A mais vendida.',
    },
  },
  {
    key: 'premium',
    fallbackPrice: 54.90,
    name: { es: 'Figura Premium personalizada', en: 'Custom Premium figurine', it: 'Statuetta Premium personalizzata', pt: 'Figura Premium personalizada' },
    desc: {
      es: 'Figura coleccionable Premium para Primera Comunión con proporciones estilizadas y acabado superior. Personalizada con foto.',
      en: 'Premium collectible figurine for First Communion with stylized proportions and superior finish.',
      it: 'Statuetta Premium da collezione per Prima Comunione con proporzioni stilizzate e finitura superiore.',
      pt: 'Figura Premium de coleção para Primeira Comunhão com proporções estilizadas e acabamento superior.',
    },
  },
  {
    key: 'pareja',
    fallbackPrice: 74.90,
    name: { es: 'Figura Pareja (dos figuras)', en: 'Couple figurines (two pieces)', it: 'Statuetta Coppia (due pezzi)', pt: 'Figura Casal (duas figuras)' },
    desc: {
      es: 'Pareja de figuras coleccionables para Primera Comunión. Dos hermanos, primos o amigos. Personalizadas con fotos.',
      en: 'Pair of collectible figurines for First Communion. Two siblings, cousins or friends. Personalized.',
      it: 'Coppia di statuette da collezione per Prima Comunione. Due fratelli, cugini o amici. Personalizzate.',
      pt: 'Par de figuras de coleção para Primeira Comunhão. Dois irmãos, primos ou amigos. Personalizadas.',
    },
  },
];

interface AggregateRating {
  ratingValue: number;
  reviewCount: number;
}

export function ProductSchema() {
  const { i18n } = useTranslation();
  const lang = (i18n.language?.split('-')[0] || 'es') as 'es' | 'en' | 'it' | 'pt';
  const [prices, setPrices] = useState<Record<string, number>>({});
  const [agg, setAgg] = useState<AggregateRating | null>(null);

  useEffect(() => {
    // Precios desde site_config/prices via endpoint público (si existe).
    // Si falla, usamos fallback hardcoded.
    fetch('/api/site/prices')
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setPrices(d); })
      .catch(() => {});
    // Agregado de reseñas para AggregateRating
    fetch('/api/reviews/aggregate')
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && d.reviewCount > 0) setAgg(d); })
      .catch(() => {});
  }, []);

  const products = PRODUCTS.map(p => {
    const price = (prices as any)[p.key] ?? p.fallbackPrice;
    return {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: p.name[lang] || p.name.es,
      description: p.desc[lang] || p.desc.es,
      image: DEFAULT_IMAGE,
      brand: { '@type': 'Brand', name: 'Funcomunión' },
      category: 'Figuras coleccionables · Primera Comunión',
      offers: {
        '@type': 'Offer',
        url: SITE_URL,
        priceCurrency: 'EUR',
        price: price.toFixed(2),
        priceValidUntil: `${new Date().getFullYear() + 1}-12-31`,
        availability: 'https://schema.org/InStock',
        itemCondition: 'https://schema.org/NewCondition',
        seller: { '@type': 'Organization', name: 'Funcomunión' },
        shippingDetails: {
          '@type': 'OfferShippingDetails',
          shippingRate: { '@type': 'MonetaryAmount', value: 12, currency: 'EUR' },
          shippingDestination: { '@type': 'DefinedRegion', addressCountry: 'ES' },
          deliveryTime: {
            '@type': 'ShippingDeliveryTime',
            handlingTime: { '@type': 'QuantitativeValue', minValue: 5, maxValue: 7, unitCode: 'DAY' },
            transitTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 3, unitCode: 'DAY' },
          },
        },
      },
      ...(agg
        ? {
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: agg.ratingValue.toFixed(1),
              reviewCount: agg.reviewCount,
              bestRating: 5,
              worstRating: 1,
            },
          }
        : {}),
    };
  });

  return (
    <Helmet>
      {products.map((p, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(p)}</script>
      ))}
    </Helmet>
  );
}
